import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Table } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiPlus, FiEdit2, FiCopy } from "react-icons/fi";
import { postApiCall, getApiCall } from "../../../service/httpCall";
import { API_WEBHOOK_CREATE, API_WEBHOOK_REQUEST_PARAMETERS, API_WEBHOOK_UPDATE, API_WEBHOOK_GET_ONE } from "../../../service/url";
import { ROUTES } from "../../../utilities/constant";
import Loader from "../../../components/partials/components/loader";
import { toast } from "react-toastify";
import { fetchAndSetFlashMessage } from '../../../service/fetchFlashMessage';
import {FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '../../../utilities/constant';

const CreateManageWebhook = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;
  const previousIdRef = useRef(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingParams, setLoadingParams] = useState(true);
  const [loadingWebhook, setLoadingWebhook] = useState(isEditMode);
  const [alertMessage, setAlertMessage] = useState(null);
  
  // Webhook details state
  const [webhookData, setWebhookData] = useState({
    webhook_name: "",
    url: "",
    event: "",
    is_default: false,
  });

  // Error states for real-time validation
  const [webhookNameError, setWebhookNameError] = useState("");
  const [webhookUrlError, setWebhookUrlError] = useState("");
  const [headerParamError, setHeaderParamError] = useState("");
  const [headerValueError, setHeaderValueError] = useState("");
  const [bodyParamError, setBodyParamError] = useState("");
  const [bodyValueError, setBodyValueError] = useState("");

  // Headers state
  const [headerParam, setHeaderParam] = useState("");
  const [headerValue, setHeaderValue] = useState("");
  const [headers, setHeaders] = useState([
    { parameter: "Content-Type", value: "application/json", protected: true }
  ]);
  const [editingHeaderIndex, setEditingHeaderIndex] = useState(null);
  const [editHeaderParam, setEditHeaderParam] = useState("");
  const [editHeaderValue, setEditHeaderValue] = useState("");

  // Body state
  const [bodyParam, setBodyParam] = useState("");
  const [bodyValue, setBodyValue] = useState("");
  const [bodyParams, setBodyParams] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editParam, setEditParam] = useState("");
  const [editValue, setEditValue] = useState("");
  const [webhookBodyData, setWebhookBodyData] = useState(null);
  const [hasMergedBodyData, setHasMergedBodyData] = useState(false);

  // Fetch request parameters from backend
  useEffect(() => {
    setLoadingParams(true);
    getApiCall(API_WEBHOOK_REQUEST_PARAMETERS, {})
      .then((response) => {
        console.log("API Response:", response);
        // Handle the API response structure
        let params = [];
        
        // The response should be the data array directly (based on getApiCall implementation)
        // But handle both cases: direct array or wrapped in object
        let dataArray = [];
        
        if (Array.isArray(response)) {
          dataArray = response;
        } else if (response && typeof response === 'object') {
          // If response is wrapped, extract data array
          dataArray = response.data || response.parameters || response.params || response.items || [];
        }
        
        // Map the data array to our format
        params = dataArray.map(param => {
          if (typeof param === 'string') {
            return {
              parameter: param,
              value: param,
              checked: !isEditMode // Auto-check in create mode, unchecked in edit mode
            };
          } else if (typeof param === 'object' && param !== null) {
            // Handle the API structure: { id, heading, parameter_name }
            // Use parameter_name as the parameter name, and parameter_name as the value (or heading as fallback)
            const paramName = param.parameter_name || param.heading || param.parameter || param.name || param.key || '';
            // For value, use parameter_name (which represents the variable name like {campaign_id})
            const paramValue = param.parameter_name || param.heading || param.value || param.parameter || param.name || '';
            return {
              parameter: paramName,
              value: paramValue,
              checked: param.checked !== undefined ? param.checked : !isEditMode // Auto-check in create mode, unchecked in edit mode
            };
          }
          return null;
        }).filter(Boolean);
        
        console.log("Parsed params:", params);
        
        setBodyParams(params);
        
        setLoadingParams(false);
      })
      .catch((err) => {
        console.error("Error fetching request parameters:", err);
        toast.error("Failed to load request parameters");
        setLoadingParams(false);
      });
    fetchAndSetFlashMessage(
            msg => msg.module_name === MODULE_ACL.WEBHOOK && msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD,
            setAlertMessage
    );
  }, []);

  // Fetch webhook data for edit mode
  useEffect(() => {
    if (isEditMode && id) {
      setLoadingWebhook(true);
      const url = API_WEBHOOK_GET_ONE.replace(":id", id);
      getApiCall(url, {})
        .then((resp) => {
          // getApiCall already returns response.data.data
          // Handle response - could be array or single object or Sequelize model
          let webhook = resp;
          if (Array.isArray(resp) && resp.length > 0) {
            webhook = resp[0];
          }
          
          console.log("Fetched webhook data:", webhook);
          
          if (webhook) {
            // Extract data from Sequelize model if needed
            const webhookDataObj = webhook.dataValues || webhook;
            
            console.log("Processing webhook data object:", webhookDataObj);
            
            // Handle mark_as_default - could be 1, 0, true, false, "1", "0", or null/undefined
            const markAsDefault = webhookDataObj.mark_as_default;
            // Convert to boolean - handle all possible formats
            const isDefault = markAsDefault === 1 || 
                             markAsDefault === true || 
                             markAsDefault === "1" || 
                             (markAsDefault !== null && markAsDefault !== undefined && String(markAsDefault) === "1");
            
            console.log("Loaded webhook - mark_as_default value:", markAsDefault, "type:", typeof markAsDefault, "converted to is_default:", isDefault);
            
            setWebhookData({
              webhook_name: webhookDataObj.webhook_name || "",
              url: webhookDataObj.url || "",
              event: webhookDataObj.event_id === 1 ? "HANGUP" : webhookDataObj.event_id === 2 ? "DTMF" : "CONNECTED_CALLS",
              is_default: isDefault,
            });

            // Parse and set headers
            try {
              const headerData = typeof webhookDataObj.header_json === "string" 
                ? JSON.parse(webhookDataObj.header_json) 
                : webhookDataObj.header_json || {};
              const headerArray = Object.entries(headerData).map(([key, value]) => {
                // Clean header parameter names to remove invalid characters
                const cleanedKey = key === "Content-Type" ? key : key.replace(/[^a-zA-Z0-9\-]/g, '');
                return {
                  parameter: cleanedKey,
                  value: value,
                  protected: key === "Content-Type"
                };
              }).filter(({ parameter }) => parameter.length > 0); // Filter out empty parameters
              setHeaders(headerArray.length > 0 ? headerArray : [
                { parameter: "Content-Type", value: "application/json", protected: true }
              ]);
            } catch (e) {
              console.error("Error parsing headers:", e);
              setHeaders([
                { parameter: "Content-Type", value: "application/json", protected: true }
              ]);
            }

            // Store webhook body data to merge with request parameters
            setWebhookBodyData(webhookDataObj.body_json);
          }
          setLoadingWebhook(false);
        })
        .catch((err) => {
          console.error("Error fetching webhook:", err);
          toast.error("Failed to load webhook data");
          setLoadingWebhook(false);
        });
    }
  }, [id, isEditMode]);

  // Reset form when switching from edit mode to create mode
  useEffect(() => {
    // Check if we're on create route (no id)
    const isInCreateMode = !id;
    const wasInEditMode = previousIdRef.current !== undefined && previousIdRef.current !== null && previousIdRef.current !== "";
    
    // If we switched from edit mode to create mode, reset all state
    if (wasInEditMode && isInCreateMode) {
      console.log("Switching from edit to create mode - resetting form state");
      // Reset all state to initial values when switching from edit to create
      setWebhookData({
        webhook_name: "",
        url: "",
        event: "",
        is_default: false,
      });
      setHeaders([
        { parameter: "Content-Type", value: "application/json", protected: true }
      ]);
      setHeaderParam("");
      setHeaderValue("");
      setBodyParam("");
      setBodyValue("");
      setEditingIndex(null);
      setEditParam("");
      setEditValue("");
      setEditingHeaderIndex(null);
      setEditHeaderParam("");
      setEditHeaderValue("");
      setWebhookBodyData(null);
      setHasMergedBodyData(false);
      // Clear all error states
      setWebhookNameError("");
      setWebhookUrlError("");
      setHeaderParamError("");
      setHeaderValueError("");
      setBodyParamError("");
      setBodyValueError("");
      // Reset body params to checked state (for create mode, all should be checked)
      setBodyParams(prevParams => 
        prevParams.map(param => ({ ...param, checked: true }))
      );
    }
    
    // Update the ref to track current id (only update if id actually changed)
    if (previousIdRef.current !== id) {
      previousIdRef.current = id;
    }
  }, [id, location.pathname]);

  // Merge body params when both webhook body data and params are loaded (only once)
  useEffect(() => {
    const shouldMerge = isEditMode && webhookBodyData && !hasMergedBodyData;
    if (shouldMerge && bodyParams.length > 0) {
      try {
        const bodyData = typeof webhookBodyData === "string"
          ? JSON.parse(webhookBodyData)
          : webhookBodyData || {};
        
        const hasBodyKeys = Object.keys(bodyData).length > 0;
        if (!hasBodyKeys) {
          setHasMergedBodyData(true);
          return;
        }
        
        // Mark params as checked if they exist in the webhook body
        const paramsWithChecked = bodyParams.map(param => {
          const isChecked = bodyData.hasOwnProperty(param.parameter);
          // If checked and has a value in body, use that value (remove {})
          let value = param.value;
          if (isChecked && bodyData[param.parameter]) {
            const bodyValue = String(bodyData[param.parameter]);
            value = bodyValue.replace(/[{}]/g, "") || param.parameter;
          }
          return {
            ...param,
            checked: isChecked,
            value: value
          };
        });
        
        // Also add any params that might be in body but not in the params list
        const bodyParamKeys = Object.keys(bodyData);
        bodyParamKeys.forEach(key => {
          if (!paramsWithChecked.find(p => p.parameter === key)) {
            const bodyValue = String(bodyData[key]);
            const cleanValue = bodyValue.replace(/[{}]/g, "") || key;
            paramsWithChecked.push({
              parameter: key,
              value: cleanValue,
              checked: true
            });
          }
        });
        
        setBodyParams(paramsWithChecked);
        setHasMergedBodyData(true);
      } catch (e) {
        console.error("Error merging body params:", e);
        setHasMergedBodyData(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhookBodyData, isEditMode, hasMergedBodyData]);

  const handleWebhookChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWebhookData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Real-time validation for webhook name
    if (name === "webhook_name") {
      const error = validateWebhookName(value);
      setWebhookNameError(error || "");
    }

    // Real-time validation for webhook URL
    if (name === "url") {
      const error = validateWebhookUrl(value);
      setWebhookUrlError(error || "");
    }
  };

  // Validation functions
  const validateWebhookName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return "Webhook name is required";
    }
    if (trimmed.length < 3) {
      return "Webhook name must be at least 3 characters long";
    }
    if (trimmed.length > 50) {
      return "Webhook name must not exceed 50 characters";
    }
    // Allow: letters, numbers, spaces, hyphens, underscores
    const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!nameRegex.test(trimmed)) {
      return "Webhook name can only contain letters, numbers, spaces, hyphens, and underscores.";
    }
    return null;
  };

  const validateWebhookUrl = (url) => {
    const trimmed = url.trim();
    if (!trimmed) {
      return "Webhook URL is required";
    }
    // Check for spaces
    if (trimmed.includes(" ")) {
      return "Webhook URL cannot contain spaces";
    }
    // Check for javascript: protocol
    if (trimmed.toLowerCase().startsWith("javascript:")) {
      return "JavaScript URLs are not allowed";
    }
    // Must start with http:// or https://
    if (!trimmed.match(/^https?:\/\//i)) {
      return "Webhook URL must start with http:// or https://";
    }
    // Disallow localhost in production
    if (process.env.NODE_ENV === "production" && trimmed.toLowerCase().includes("localhost")) {
      return "Localhost URLs are not allowed in production";
    }
    // Validate URL format
    try {
      new URL(trimmed);
    } catch (e) {
      return "Please enter a valid Webhook URL";
    }
    return null;
  };

  const validateHeaderParameter = (param) => {
    const trimmed = param.trim();
    if (!trimmed) {
      return "Header parameter is required";
    }
    if (trimmed.length < 1) {
      return "Header parameter must be at least 1 character";
    }
    if (trimmed.length > 50) {
      return "Header parameter must not exceed 50 characters";
    }
    // Allow: letters, numbers, hyphens
    const paramRegex = /^[a-zA-Z0-9\-]+$/;
    if (!paramRegex.test(trimmed)) {
      return "Header parameter can only contain letters, numbers, and hyphens";
    }
    return null;
  };

  const validateBodyParameter = (param) => {
    const trimmed = param.trim();
    if (!trimmed) return "Body parameter is required";
    const paramRegex = /^[a-z0-9_]+$/;
    if (!paramRegex.test(trimmed)) {
      return "Body parameter can only contain lowercase letters, numbers and underscore (_). Special characters are not allowed.";
    }
    return null;
  };

  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return null;
    } catch (e) {
      return "Invalid JSON format";
    }
  };

  // Headers functions
  const handleAddHeader = () => {
    // Real-time validation
    const paramTrimmed = headerParam.trim();
    const valueTrimmed = headerValue.trim();
    
    if (!paramTrimmed) {
      setHeaderParamError("Header parameter is required");
      return;
    } else {
      setHeaderParamError("");
    }
    
    if (!valueTrimmed) {
      setHeaderValueError("Header value is required");
      return;
    } else {
      setHeaderValueError("");
    }
    
    // Clean the parameter to remove any invalid characters that might have been entered
    const cleanedParam = paramTrimmed.replace(/[^a-zA-Z0-9\-]/g, '');
    
    if (!cleanedParam) {
      setHeaderParamError("Header parameter must contain at least one valid character (letters, numbers, or hyphens)");
      return;
    }
    
    // Validate header parameter
    const paramError = validateHeaderParameter(cleanedParam);
    if (paramError) {
      setHeaderParamError(paramError);
      return;
    }
    
    // Check if parameter already exists
    if (headers.some(h => h.parameter.toLowerCase() === cleanedParam.toLowerCase() && !h.protected)) {
      setHeaderParamError("Header parameter already exists");
      return;
    }

    setHeaders([...headers, { parameter: cleanedParam, value: valueTrimmed, protected: false }]);
    setHeaderParam("");
    setHeaderValue("");
    setHeaderParamError("");
    setHeaderValueError("");
  };

  const handleEditHeader = (index) => {
    const header = headers[index];
    if (header.protected) return;
    setEditingHeaderIndex(index);
    setEditHeaderParam(header.parameter);
    setEditHeaderValue(header.value);
  };

  const handleSaveHeaderEdit = () => {
    const paramTrimmed = editHeaderParam.trim();
    const valueTrimmed = editHeaderValue.trim();
    
    if (!paramTrimmed) {
      toast.error("Header parameter is required");
      return;
    }
    
    if (!valueTrimmed) {
      toast.error("Header value is required");
      return;
    }
    
    // Clean the parameter
    const cleanedParam = paramTrimmed.replace(/[^a-zA-Z0-9\-]/g, '');
    
    if (!cleanedParam) {
      toast.error("Header parameter must contain at least one valid character (letters, numbers, or hyphens)");
      return;
    }
    
    // Validate header parameter
    const paramError = validateHeaderParameter(cleanedParam);
    if (paramError) {
      toast.error(paramError);
      return;
    }
    
    // Check if parameter already exists (excluding current index)
    if (headers.some((h, i) => i !== editingHeaderIndex && h.parameter.toLowerCase() === cleanedParam.toLowerCase() && !h.protected)) {
      toast.warning("Header parameter already exists");
      return;
    }

    const updatedHeaders = [...headers];
    updatedHeaders[editingHeaderIndex] = {
      parameter: cleanedParam,
      value: valueTrimmed,
      protected: headers[editingHeaderIndex].protected
    };
    setHeaders(updatedHeaders);
    setEditingHeaderIndex(null);
    setEditHeaderParam("");
    setEditHeaderValue("");
  };

  const handleCancelHeaderEdit = () => {
    setEditingHeaderIndex(null);
    setEditHeaderParam("");
    setEditHeaderValue("");
  };

  const handleDeleteHeader = (index) => {
    if (headers[index].protected) return;
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const copyHeadersJSON = () => {
    const headersObj = headers.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    navigator.clipboard.writeText(JSON.stringify(headersObj, null, 2));
    toast.success("Headers JSON copied to clipboard");
  };

  // Body functions
  const handleAddBodyParam = () => {
    const param = bodyParam.trim();
    const value = bodyValue.trim();

    // Real-time validation
    if (!param) {
      setBodyParamError("Body parameter is required");
      return;
    } else {
      setBodyParamError("");
    }

    if (!value) {
      setBodyValueError("Body value is required");
      return;
    } else {
      setBodyValueError("");
    }

    if (value === "0" || value === "false") {
      setBodyValueError("Falsy values are not allowed in body parameters");
      return;
    }

    const paramError = validateBodyParameter(param);
    if (paramError) {
      setBodyParamError(paramError);
      return;
    }

    if (bodyParams.some(p => p.parameter.toLowerCase() === param.toLowerCase())) {
      setBodyParamError("Body parameter already exists");
      return;
    }

    setBodyParams([
      ...bodyParams,
      { parameter: param, value: value, checked: true }
    ]);

    setBodyParam("");
    setBodyValue("");
    setBodyParamError("");
    setBodyValueError("");
  };

  const handleToggleBodyParam = (index) => {
    const updatedParams = [...bodyParams];
    updatedParams[index].checked = !updatedParams[index].checked;
    setBodyParams(updatedParams);
  };

  const handleToggleAllBodyParams = (checked) => {
    const updatedParams = bodyParams.map(param => ({
      ...param,
      checked: checked
    }));
    setBodyParams(updatedParams);
  };

  const handleEditBodyParam = (index) => {
    const param = bodyParams[index];
    // Validate the body parameter before allowing edit
    const paramError = validateBodyParameter(param.parameter);
    if (paramError) {
      toast.warning(`Current body parameter has validation issues: ${paramError}. Please fix it.`);
    }
    setEditingIndex(index);
    setEditParam(param.parameter);
    setEditValue(param.value);
  };

  const handleSaveEdit = () => {
    const param = editParam.trim();
    const value = editValue.trim();

    if (!param || !value) {
      toast.warning("Parameter and value are required. Falsy values are not allowed.");
      return;
    }

    if (!param || !value || value === "0" || value === "false") {
      toast.warning("Falsy values are not allowed in body parameters.");
      return;
    }

    const paramError = validateBodyParameter(param);
    if (paramError) {
      toast.error(paramError);
      return;
    }

    const updatedParams = [...bodyParams];
    updatedParams[editingIndex] = {
      parameter: param,
      value: value,
      checked: updatedParams[editingIndex].checked
    };

    setBodyParams(updatedParams);
    setEditingIndex(null);
    setEditParam("");
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditParam("");
    setEditValue("");
  };

  const copyBodyJSON = () => {
    const bodyObj = bodyParams
      .filter(p => p.checked)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});
    navigator.clipboard.writeText(JSON.stringify(bodyObj, null, 2));
    toast.success("Body JSON copied to clipboard");
  };

  // Generate JSON previews
  const getHeadersJSON = () => {
    const headersObj = headers.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    return JSON.stringify(headersObj, null, 2);
  };

  const getBodyJSON = () => {
    const bodyObj = bodyParams
      .filter(p => p.checked)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});
    return JSON.stringify(bodyObj, null, 2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate webhook name
    const nameError = validateWebhookName(webhookData.webhook_name);
    if (nameError) {
      toast.error(nameError);
      return;
    }

    // Validate webhook URL
    const urlError = validateWebhookUrl(webhookData.url);
    if (urlError) {
      toast.error(urlError);
      return;
    }

    // Validate event
    if (!webhookData.event) {
      toast.error("Please select an event");
      return;
    }
    const allowedEvents = ["HANGUP", "DTMF", "CONNECTED_CALLS"];
    if (!allowedEvents.includes(webhookData.event)) {
      toast.error("Please select a valid event");
      return;
    }

    // Validate headers JSON
    const headersJSON = getHeadersJSON();
    const headersError = validateJSON(headersJSON);
    if (headersError) {
      toast.error(`Headers JSON is invalid: ${headersError}`);
      return;
    }

    // Clean and validate each header parameter
    const cleanedHeaders = headers.map(header => {
      if (header.protected) return header;
      const cleanedParam = header.parameter.replace(/[^a-zA-Z0-9\-]/g, '');
      if (cleanedParam !== header.parameter && cleanedParam.length > 0) {
        toast.warning(`Header parameter "${header.parameter}" contains invalid characters and will be saved as "${cleanedParam}"`);
      }
      return {
        ...header,
        parameter: cleanedParam || header.parameter
      };
    }).filter(header => header.parameter.length > 0 || header.protected);
    
    // Validate each cleaned header parameter
    for (const header of cleanedHeaders) {
      const paramError = validateHeaderParameter(header.parameter);
      if (paramError && !header.protected) {
        toast.error(`Header parameter "${header.parameter}": ${paramError}`);
        return;
      }
    }

    // Validate body JSON
    const bodyJSON = getBodyJSON();
    const bodyError = validateJSON(bodyJSON);
    if (bodyError) {
      toast.error(`Body JSON is invalid: ${bodyError}`);
      return;
    }

    // Validate each body parameter
    const checkedBodyParams = bodyParams.filter(p => p.checked);
    for (const param of checkedBodyParams) {
      const paramError = validateBodyParameter(param.parameter);
      if (paramError) {
        toast.error(`Body parameter "${param.parameter}": ${paramError}`);
        return;
      }
    }

    setLoading(true);

    // Prepare headers object (include all cleaned headers for storage)
    const allHeaders = cleanedHeaders.reduce((acc, h) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});

    // Prepare body object (only include checked parameters)
    // Format: { "parameter_name": "{parameter_name}" } for JSON preview
    // The value should be wrapped in curly braces to indicate it's a template variable
    const bodyObj = bodyParams
      .filter(p => p.checked && p.parameter && p.value)
      .reduce((acc, p) => {
        acc[p.parameter] = `{${p.value}}`;
        return acc;
      }, {});

    // Prepare payload according to backend schema
    const payload = {
      webhook_name: webhookData.webhook_name.trim(),
      url: webhookData.url.trim(),
      event_id: webhookData.event === "HANGUP" ? 1 : webhookData.event === "DTMF" ? 2 : 3,
      mark_as_default: webhookData.is_default ? 1 : 0, // INTEGER, not string
      header_json: JSON.stringify(allHeaders), // JSON string (includes all headers)
      body_json: JSON.stringify(bodyObj), // JSON string
    };

    const apiCall = isEditMode
      ? postApiCall(API_WEBHOOK_UPDATE.replace(":id", id), payload)
      : postApiCall(API_WEBHOOK_CREATE, payload);

    apiCall
      .then((resp) => {
        toast.success(isEditMode ? "Webhook updated successfully!" : "Webhook created successfully!");
        setTimeout(() => {
          navigate(`/${ROUTES.MANAGE_WEBHOOK.LIST}`);
        }, 2000);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error(typeof err === "string" ? err : JSON.stringify(err));
        setLoading(false);
      });
  };

  if (loading || loadingParams || loadingWebhook) return <Loader />;

  return (
    <>
    {alertMessage && <marquee className="fade alert alert-success show" style={{marginTop : '5px'}}>{alertMessage}</marquee>}
    <div className="px-3">
      <Row className="mx-0">
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h3 className="mb-2">{isEditMode ? "EDIT WEBHOOK" : "ADD WEBHOOK"}</h3>
              <hr className="mt-2 mb-4" />
              <Form onSubmit={handleSubmit}>
                {/* Webhook Details Section */}
                <div className="mb-4">
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Webhook Name <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Control
                          type="text"
                          name="webhook_name"
                          value={webhookData.webhook_name}
                          onChange={handleWebhookChange}
                          onBlur={(e) => {
                            const error = validateWebhookName(e.target.value);
                            setWebhookNameError(error || "");
                          }}
                          placeholder="Webhook Name"
                          required
                          minLength={3}
                          maxLength={50}
                          pattern="[a-zA-Z0-9\s\-_]+"
                          title="Webhook name can only contain letters, numbers, spaces, hyphens, and underscores."
                          isInvalid={!!webhookNameError}
                        />
                        {webhookNameError && (
                          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                            {webhookNameError}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Webhook URL<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Control
                          type="url"
                          name="url"
                          value={webhookData.url}
                          onChange={handleWebhookChange}
                          onBlur={(e) => {
                            const error = validateWebhookUrl(e.target.value);
                            setWebhookUrlError(error || "");
                          }}
                          placeholder="Webhook URL"
                          required
                          title="Must start with http:// or https://"
                          isInvalid={!!webhookUrlError}
                        />
                        {webhookUrlError && (
                          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                            {webhookUrlError}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Event<span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                        <Form.Select
                          name="event"
                          value={webhookData.event}
                          onChange={handleWebhookChange}
                          required
                        >
                          <option value="">Select Events</option>
                          <option value="HANGUP">HANGUP</option>
                          <option value="DTMF">DTMF</option>
                          <option value="CONNECTED_CALLS">CONNECTED_CALLS</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>&nbsp;</Form.Label>
                        <Form.Check
                          type="checkbox"
                          name="is_default"
                          label="Make this as default"
                          checked={webhookData.is_default}
                          onChange={handleWebhookChange}
                          className="mt-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Headers Section */}
                <div className="mb-4">
                  <h5 className="mb-3">Headers</h5>
                  <Row>
                    <Col md={8}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Parameter"
                            value={headerParam}
                            onChange={(e) => {
                              // Only allow letters, numbers, and hyphens
                              const value = e.target.value.replace(/[^a-zA-Z0-9\-]/g, '');
                              setHeaderParam(value);
                              // Real-time validation
                              if (!value.trim()) {
                                setHeaderParamError("Header parameter is required");
                              } else {
                                const error = validateHeaderParameter(value);
                                setHeaderParamError(error || "");
                              }
                            }}
                            onBlur={(e) => {
                              if (!e.target.value.trim()) {
                                setHeaderParamError("Header parameter is required");
                              }
                            }}
                            maxLength={50}
                            pattern="[a-zA-Z0-9\-]+"
                            title="Header parameter can only contain letters, numbers, and hyphens (1-50 characters)"
                            isInvalid={!!headerParamError}
                          />
                          {headerParamError && (
                            <Form.Control.Feedback type="invalid" style={{ display: "block", fontSize: "0.875rem" }}>
                              {headerParamError}
                            </Form.Control.Feedback>
                          )}
                        </Col>
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Value"
                            value={headerValue}
                            onChange={(e) => {
                              setHeaderValue(e.target.value);
                              // Real-time validation
                              if (!e.target.value.trim()) {
                                setHeaderValueError("Header value is required");
                              } else {
                                setHeaderValueError("");
                              }
                            }}
                            onBlur={(e) => {
                              if (!e.target.value.trim()) {
                                setHeaderValueError("Header value is required");
                              }
                            }}
                            isInvalid={!!headerValueError}
                          />
                          {headerValueError && (
                            <Form.Control.Feedback type="invalid" style={{ display: "block", fontSize: "0.875rem" }}>
                              {headerValueError}
                            </Form.Control.Feedback>
                          )}
                        </Col>
                        <Col md={2}>
                          <Button variant="primary" onClick={handleAddHeader}>
                            <FiPlus />
                          </Button>
                        </Col>
                      </Row>

                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headers.map((header, index) => (
                            <tr key={index}>
                              <td>
                                {editingHeaderIndex === index ? (
                                  <Form.Control
                                    type="text"
                                    value={editHeaderParam}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^a-zA-Z0-9\-]/g, '');
                                      setEditHeaderParam(value);
                                    }}
                                    style={{ minWidth: "150px" }}
                                    maxLength={50}
                                    pattern="[a-zA-Z0-9\-]+"
                                  />
                                ) : (
                                  <span>{header.parameter}</span>
                                )}
                              </td>
                              <td>
                                {editingHeaderIndex === index ? (
                                  <Form.Control
                                    type="text"
                                    value={editHeaderValue}
                                    onChange={(e) => setEditHeaderValue(e.target.value)}
                                    style={{ minWidth: "150px" }}
                                  />
                                ) : (
                                  <span>{header.value}</span>
                                )}
                              </td>
                              <td>
                                {header.protected ? (
                                  <span className="text-muted">Protected</span>
                                ) : editingHeaderIndex === index ? (
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={handleSaveHeaderEdit}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={handleCancelHeaderEdit}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleEditHeader(index)}
                                      className="p-0"
                                    >
                                      <FiEdit2 className="text-warning" />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleDeleteHeader(index)}
                                      className="p-0"
                                    >
                                      <span className="text-danger">Delete</span>
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>Headers JSON</h6>
                        <Button variant="link" size="sm" onClick={copyHeadersJSON} className="p-0">
                          <FiCopy />
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        value={getHeadersJSON()}
                        readOnly
                        style={{ fontFamily: "monospace", fontSize: "12px" }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* Body Section */}
                <div className="mb-4">
                  <h5 className="mb-3">Body</h5>
                  <Row>
                    <Col md={8}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Parameter"
                            value={bodyParam}
                            onChange={(e) => {
                              // Remove everything except a-z, 0-9 and _
                              const value = e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9_]/g, '');
                              setBodyParam(value);
                              // Real-time validation
                              if (!value.trim()) {
                                setBodyParamError("Body parameter is required");
                              } else {
                                const error = validateBodyParameter(value);
                                setBodyParamError(error || "");
                              }
                            }}
                            onBlur={(e) => {
                              if (!e.target.value.trim()) {
                                setBodyParamError("Body parameter is required");
                              }
                            }}
                            pattern="[a-z0-9_]+"
                            title="Body parameter can only contain lowercase letters, numbers, and underscores (no spaces)"
                            isInvalid={!!bodyParamError}
                          />
                          {bodyParamError && (
                            <Form.Control.Feedback type="invalid" style={{ display: "block", fontSize: "0.875rem" }}>
                              {bodyParamError}
                            </Form.Control.Feedback>
                          )}
                        </Col>
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Value"
                            value={bodyValue}
                            onChange={(e) => {
                              setBodyValue(e.target.value);
                              // Real-time validation
                              if (!e.target.value.trim()) {
                                setBodyValueError("Body value is required");
                              } else if (e.target.value.trim() === "0" || e.target.value.trim() === "false") {
                                setBodyValueError("Falsy values are not allowed in body parameters");
                              } else {
                                setBodyValueError("");
                              }
                            }}
                            onBlur={(e) => {
                              if (!e.target.value.trim()) {
                                setBodyValueError("Body value is required");
                              } else if (e.target.value.trim() === "0" || e.target.value.trim() === "false") {
                                setBodyValueError("Falsy values are not allowed in body parameters");
                              }
                            }}
                            isInvalid={!!bodyValueError}
                          />
                          {bodyValueError && (
                            <Form.Control.Feedback type="invalid" style={{ display: "block", fontSize: "0.875rem" }}>
                              {bodyValueError}
                            </Form.Control.Feedback>
                          )}
                        </Col>
                        <Col md={2}>
                          <Button variant="primary" onClick={handleAddBodyParam}>
                            <FiPlus />
                          </Button>
                        </Col>
                      </Row>

                      <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6" }}>
                        <Table striped bordered hover style={{ marginBottom: 0 }}>
                          <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                            <tr>
                              <th>
                                <Form.Check
                                  type="checkbox"
                                  checked={bodyParams.length > 0 && bodyParams.every(p => p.checked)}
                                  onChange={(e) => handleToggleAllBodyParams(e.target.checked)}
                                />
                              </th>
                              <th>Parameter</th>
                              <th>Value</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingParams ? (
                              <tr>
                                <td colSpan={4} className="text-center">
                                  <Loader />
                                </td>
                              </tr>
                            ) : bodyParams.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted">
                                  No parameters available
                                </td>
                              </tr>
                            ) : (
                              bodyParams.map((param, index) => (
                                <tr key={index}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={!!param.checked}
                                      onChange={() => handleToggleBodyParam(index)}
                                    />
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <Form.Control
                                        type="text"
                                        value={editParam}
                                        onChange={(e) => {
                                          const value = e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9_]/g, '');
                                          setEditParam(value);
                                        }}
                                        style={{ minWidth: "150px" }}
                                        pattern="[a-z0-9_]+"
                                        title="Body parameter can only contain lowercase letters, numbers, and underscores (no spaces)"
                                      />
                                    ) : (
                                      <span className={editingIndex === index ? "text-primary" : ""}>
                                        {param.parameter || ""}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <Form.Control
                                        type="text"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        style={{ minWidth: "150px" }}
                                      />
                                    ) : (
                                      <span>{param.value || ""}</span>
                                    )}
                                  </td>
                                  <td>
                                    {editingIndex === index ? (
                                      <div className="d-flex gap-2">
                                        <Button
                                          variant="success"
                                          size="sm"
                                          onClick={handleSaveEdit}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => handleEditBodyParam(index)}
                                        className="p-0"
                                      >
                                        <FiEdit2 className="text-warning" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>Selected Body JSON</h6>
                        <Button variant="link" size="sm" onClick={copyBodyJSON} className="p-0">
                          <FiCopy />
                        </Button>
                      </div>
                      <Form.Control
                        as="textarea"
                        rows={15}
                        value={getBodyJSON()}
                        readOnly
                        style={{ fontFamily: "monospace", fontSize: "12px" }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* Submit Button */}
                <div className="mt-4">
                  <Button variant="primary" type="submit" size="md" disabled={loading}>
                    {isEditMode ? "Update Webhook" : "Add Webhook"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
    </>
  );
};

export default CreateManageWebhook;

